import { useEffect, useState } from 'react';

import { describeDiffForNotes, type SolutionDiff } from './solution-diff';

/** One note per release and diff, for the session. */
const cache = new Map<string, string>();

/**
 * The AI-drafted note for this publish (`/ai/deploy/release-notes`), or
 * `null` while it's being written or when it couldn't be — the panel shows
 * the plain diff summary then.
 */
export const useReleaseNotes = ({
  version,
  baseline,
  diff,
  extra = '',
}: {
  version: string;
  baseline: string;
  diff: SolutionDiff | null;
  /** More changes to describe, one per line — e.g. action code. */
  extra?: string;
}) => {
  const changes = diff
    ? [describeDiffForNotes(diff), extra].filter(Boolean).join('\n')
    : '';
  const key = `${version}|${baseline}|${changes}`;
  const [note, setNote] = useState<string | null>(cache.get(key) ?? null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!diff || !diff.changes.length) {
      setNote(null);
      return undefined;
    }
    const hit = cache.get(key);
    if (hit) {
      setNote(hit);
      return undefined;
    }
    let cancelled = false;
    setNote(null);
    // Wait for the draft to settle: edits change the diff on every keystroke.
    const timer = setTimeout(() => {
      setLoading(true);
      fetch('/ai/deploy/release-notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ version, baseline, changes }),
      })
        .then(async (response) => {
          const json = (await response.json().catch(() => null)) as {
            note?: string;
          } | null;
          if (!response.ok || !json?.note) throw new Error('No note');
          return json.note;
        })
        .then((text) => {
          cache.set(key, text);
          if (!cancelled) setNote(text);
        })
        .catch(() => {
          // The summary below stands in; nothing to report.
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 800);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `key` stands for the version, baseline and diff.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { note, loading };
};
